import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Users, Star, CreditCard, Zap, Award, Calendar,
  GraduationCap, ExternalLink, Bell, Clock, CheckCircle, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';

const typeConfig = {
  application: { icon: Users, color: 'text-chart-1', bg: 'bg-chart-1/10', label: 'Application' },
  milestone: { icon: Star, color: 'text-chart-4', bg: 'bg-chart-4/10', label: 'Milestone' },
  payment: { icon: CreditCard, color: 'text-chart-3', bg: 'bg-chart-3/10', label: 'Payment' },
  match: { icon: Zap, color: 'text-primary', bg: 'bg-primary/10', label: 'AI Match' },
  certificate: { icon: Award, color: 'text-chart-5', bg: 'bg-chart-5/10', label: 'Certificate' },
  event: { icon: Calendar, color: 'text-chart-2', bg: 'bg-chart-2/10', label: 'Event' },
  mentor: { icon: GraduationCap, color: 'text-warning', bg: 'bg-warning/10', label: 'Mentor' },
};

export default function NotificationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notifications, markRead } = useApp();

  const notif = notifications.find(n => String(n._id || n.id) === String(id));

  useEffect(() => {
    const notifId = notif?._id || notif?.id;
    if (notif && !notif.read && notifId) markRead(notifId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notif?._id, notif?.id]);

  if (!notif) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" className="gap-2 mb-6" onClick={() => navigate('/notifications')} data-testid="back-to-notifications">
          <ArrowLeft className="w-4 h-4" /> Back to Notifications
        </Button>
        <div className="text-center py-20">
          <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
          <p className="text-muted-foreground">Notification not found.</p>
        </div>
      </div>
    );
  }

  const config = typeConfig[notif.type] || typeConfig.application;
  const notifId   = notif._id || notif.id;
  const prevIndex = notifications.findIndex(n => (n._id || n.id) === notifId);
  const prev = prevIndex > 0 ? notifications[prevIndex - 1] : null;
  const next = prevIndex < notifications.length - 1 ? notifications[prevIndex + 1] : null;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="notification-detail-page">
      <Button variant="ghost" size="sm" className="gap-2 -ml-2" onClick={() => navigate('/notifications')} data-testid="back-to-notifications">
        <ArrowLeft className="w-4 h-4" /> Back to Notifications
      </Button>

      <Card className="border-border overflow-hidden">
        <CardContent className="p-0">
          {/* Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-start gap-4">
              <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0', config.bg)}>
                <config.icon className={cn('w-6 h-6', config.color)} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium uppercase tracking-wider', config.bg, config.color)}>
                    {config.label}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {notif.time}
                  </span>
                  <span className="text-xs text-success flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Read
                  </span>
                </div>
                <h1 className="text-xl font-bold text-foreground">{notif.title}</h1>
                <p className="text-sm text-muted-foreground mt-1">{notif.message}</p>
              </div>
            </div>
          </div>

          {/* Detail body */}
          <div className="p-6 space-y-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Details</p>
              <p className="text-sm text-foreground leading-relaxed">{notif.detail || notif.message}</p>
            </div>

            {notif.link && (
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-primary" /> Jump to source
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Open the page where this notification originated.</p>
                </div>
                <Button
                  className="gradient-primary text-primary-foreground border-0 gap-1.5 flex-shrink-0"
                  onClick={() => navigate(notif.link)}
                  data-testid="notification-backlink-btn"
                >
                  {notif.linkLabel || 'Open'} <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Prev / Next */}
      <div className="flex items-center justify-between">
        {prev ? (
          <Link to={`/notifications/${prev._id || prev.id}`} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2" data-testid="notification-prev">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Previous:</span> {prev.title}
          </Link>
        ) : <span />}
        {next && (
          <Link to={`/notifications/${next._id || next.id}`} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 text-right" data-testid="notification-next">
            <span className="hidden sm:inline">Next:</span> {next.title}
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}
